import { useEffect, useMemo, useState } from 'react';
import { useActivities } from '@/hooks/useActivities';
import { useNonBusinessDays } from '@/hooks/useNonBusinessDays';
import { useAppQueryParams } from '@/hooks/useAppQueryParams';
import { getMarketClose } from '@/lib/marketClose';
import {
  getCurrencyDisplayClass,
  getCurrencyString,
  getMonthName,
  getTimeFromMilitaryTime,
} from '@/lib/formatting';
import { FundListSortColumn } from '@/types/fund.types';
import type { CustomerActivityListSortAndFilter } from '@/types/fund.types';
import type { ActivityModel } from '@/types/model.types';
import { SortHeader } from '@/components/SortHeader';
import { FundTrades } from '@/components/FundTrades';
import { PageSpinner } from '@/components/PageSpinner';
import { ErrorState } from '@/components/ErrorState';
import { useFundListState } from '@/state/FundListState';

/** Port of home.page.ts / home.page.html. */
export default function HomePage() {
  const { activityDate, tenant } = useAppQueryParams();
  // Lives above the routes, so it's still here after a trip to another page.
  const { sortColumn, sortDirection, expandedFundIds, toggleSort, toggleFund } =
    useFundListState();

  const filter: CustomerActivityListSortAndFilter = useMemo(
    () => ({ sortColumn, sortDirection, tradeDateAsYyyyMmDd: activityDate, tenant }),
    [sortColumn, sortDirection, activityDate, tenant],
  );

  const {
    activities,
    dateQueried,
    lastTradeTime,
    isPending,
    isError,
    error,
    isFetching,
    refetch,
  } = useActivities(filter);
  const { data: nonBusinessDays } = useNonBusinessDays();

  if (isPending) return <PageSpinner message="Loading funds..." />;
  if (isError) {
    return <ErrorState title="Could not load funds" error={error} onRetry={() => void refetch()} />;
  }

  return (
    <div className="mx-auto max-w-7xl px-3 pb-8 lg:px-4">
      <StatusBar
        dateQueried={dateQueried}
        lastTradeTime={lastTradeTime}
        nonBusinessDays={nonBusinessDays ?? {}}
        isRefreshing={isFetching}
        onRefresh={() => void refetch()}
      />

      {/* Sticky column headers. size-lg="2" etc. become col-span utilities. */}
      <div className="sticky top-14 z-20 grid grid-cols-12 items-center gap-1 border-b border-slate-300 bg-white py-2 lg:top-[70px] lg:gap-2">
        <div className="col-span-5 lg:col-span-2">
          <SortHeader
            label="Ticker"
            column={FundListSortColumn.TICKER}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="hidden lg:col-span-3 lg:block">
          <SortHeader
            label="Fund Name"
            column={FundListSortColumn.FUND_NAME}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="col-span-5 flex justify-end lg:col-span-3">
          <SortHeader
            label="Activity"
            column={FundListSortColumn.ACTIVITY}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="hidden justify-end lg:col-span-2 lg:flex">
          <SortHeader
            label="Largest Trade"
            column={FundListSortColumn.LARGEST_TRADE}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="col-span-2 text-center font-semibold text-slate-900 lg:col-span-2">
          <span title="More purchases or sales for the day">Trend</span>
        </div>
      </div>

      {activities.length === 0 ? (
        <p className="py-10 text-center text-gray1">No trades received on {dateQueried}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {activities.map((activity) => {
            const fundId = activity.fund!.fundId;
            const isOpen = expandedFundIds.has(fundId);

            return (
              <li key={activity.activityId}>
                <div className="grid grid-cols-12 items-center gap-1 py-2 hover:bg-slate-100 lg:gap-2">
                  <div className="col-span-5 flex items-center gap-1 lg:col-span-2">
                    <button
                      type="button"
                      onClick={() => toggleFund(fundId)}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Hide' : 'Show'} trades for ${activity.fund!.ticker}`}
                      className="rounded p-1 text-primary hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    >
                      <Chevron open={isOpen} />
                    </button>
                    <span className="font-medium text-slate-900">{activity.fund!.ticker}</span>
                  </div>

                  <div className="hidden truncate text-slate-700 lg:col-span-3 lg:block">
                    {activity.fund!.name}
                  </div>

                  <div className="col-span-5 pr-1 text-right lg:col-span-3">
                    <span className={`${getCurrencyDisplayClass(activity.aggregateAmount)} whitespace-nowrap tabular-nums`}>
                      {getCurrencyString(activity.aggregateAmount)}
                    </span>
                  </div>

                  <div className="hidden text-right lg:col-span-2 lg:block">
                    <span className={`${getCurrencyDisplayClass(activity.largestTradeAmount)} whitespace-nowrap tabular-nums`}>
                      {getCurrencyString(activity.largestTradeAmount)}
                    </span>
                  </div>

                  <div className="col-span-2 flex justify-center lg:col-span-2">
                    <TrendArrow activity={activity} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-b border-slate-200 pb-2">
                    <FundTrades fundId={fundId} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface StatusBarProps {
  dateQueried: string;
  lastTradeTime: string | null;
  nonBusinessDays: Record<string, { date: string; description: string }>;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function StatusBar({
  dateQueried,
  lastTradeTime,
  nonBusinessDays,
  isRefreshing,
  onRefresh,
}: StatusBarProps) {
  // The countdown to close is live, so tick it once a minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const marketClose = getMarketClose(now, nonBusinessDays);

  const year = dateQueried ? dateQueried.substring(0, 4) : '';
  const month = dateQueried ? dateQueried.substring(5, 7) : '';
  const day = dateQueried ? dateQueried.substring(8, 10) : '';
  const time = lastTradeTime ? getTimeFromMilitaryTime(lastTradeTime) : '';

  const shortText =
    dateQueried && lastTradeTime ? `${time}, ${month}/${day}/${year}` : `None received on ${dateQueried}`;
  const longText =
    dateQueried && lastTradeTime
      ? `${time}, ${getMonthName(month)} ${day}, ${year}`
      : `No intermediary/platform trades received on ${dateQueried}`;

  return (
    <div className="grid gap-x-4 gap-y-1 py-3 text-sm sm:grid-cols-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="Refresh data"
          aria-label="Refresh data"
          className="rounded p-1 text-primary hover:bg-black/5 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <RefreshIcon spinning={isRefreshing} />
        </button>
        <span className="font-semibold text-slate-900">
          <span className="lg:hidden">Last Trade Rcvd:</span>
          <span className="hidden lg:inline">Last Trade File Received:</span>
        </span>
        <span className="text-slate-700">
          <span className="lg:hidden">{shortText}</span>
          <span className="hidden lg:inline">{longText}</span>
        </span>
      </div>

      <div className="flex items-center gap-2 sm:justify-end">
        <span className="font-semibold text-slate-900">Market Close:</span>
        <span className="text-slate-700">{marketClose}</span>
      </div>
    </div>
  );
}

function TrendArrow({ activity }: { activity: ActivityModel }) {
  const up = activity.purchaseCount >= activity.saleCount;
  const label = up ? 'More purchases than sales' : 'More sales than purchases';

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={up ? 'trend-up' : 'trend-down'}
    />
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 12"
      aria-hidden="true"
      className={`transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <path d="M4 2l4 4-4 4z" fill="currentColor" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className={spinning ? 'animate-spin motion-reduce:animate-none' : ''}
    >
      <path d="M21 12a9 9 0 11-2.64-6.36M21 3v6h-6" />
    </svg>
  );
}
